import {useSearchParams} from "react-router-dom";
import {ChangeEvent, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {SelectChangeEvent} from "@mui/material";
import {extractEvent} from "@/lib/utils";
import {NamedValue} from "@/lib/modelstate";
import {useLocalState} from "@/lib/localstate";
import {computeRestoredState, FieldEnhancer, parseSearchParams} from "@/lib/querystate-restore";

export const useQueryState = <T extends object>({
    initialState,
    enhancer,
    localStorageName
}: {
    initialState?: T | (() => T);
    enhancer?: Record<string, FieldEnhancer>;
    localStorageName?: string;
}) => {
    const [searchParamsRaw, setSearchParamsRaw] = useSearchParams();
    const [searchParamTrigger, setSearchParamTrigger] = useState(0);
    const [store, setStore] = useLocalState<T>(localStorageName, initialState);

    const searchParamsRawIgnoreRef = useRef(false);

    const enhancerRef = useRef(enhancer);
    enhancerRef.current = enhancer;

    /**
     * Seed state computed once, synchronously, on the very first render.
     *
     * `didSeedRef` guards against re-computation on subsequent renders.
     * `seedStateRef` holds the restored state (companyId stripped) so that
     * `searchParams` can return the restored value from the first render onward —
     * before the URL has been updated by the post-mount seed effect.
     */
    const didSeedRef = useRef(false);
    const seedStateRef = useRef<Partial<T> | undefined>(undefined);

    if (!didSeedRef.current) {
        didSeedRef.current = true;
        const urlIsEmpty = searchParamsRaw.toString() === "";
        const restored = computeRestoredState(store, urlIsEmpty);
        if (restored != null) {
            seedStateRef.current = restored as Partial<T>;
        }
    }

    /**
     * Parsed search params derived from the current URL.
     *
     * On the first render, when the URL is still empty and the seed state has been
     * computed from localStorage, the seed state is returned directly so that
     * `searchParamsRef` (and therefore `getSearchParams()`) already hold the
     * restored values before the post-mount URL-update effect fires.
     */
    const searchParams = useMemo(() => {
        if (seedStateRef.current != null && searchParamsRaw.toString() === "") {
            return seedStateRef.current as T;
        }
        return parseSearchParams<T>(searchParamsRaw.entries(), enhancerRef.current) as T;
    }, [searchParamsRaw]); // enhancerRef is a stable ref — not needed in deps

    const setSearchParams = useCallback(
        (updates: Partial<T> | ((prev: T) => T)): void => {
            setSearchParamsRaw(
                (prev) => {
                    const newParams = new URLSearchParams();
                    const updatesObject =
                        typeof updates === "function"
                            ? updates(Object.fromEntries(prev.entries()) as T)
                            : (updates as T);
                    setStore(updatesObject);
                    if (updatesObject) {
                        for (const [key, value] of Object.entries(updatesObject)) {
                            if (value != null && value !== "" && !(Array.isArray(value) && value.length === 0)) {
                                if (Array.isArray(value)) {
                                    newParams.set(key, value.join(",")); // Convert array back to comma-separated string
                                } else {
                                    newParams.set(key, value.toString());
                                }
                            }
                        }
                    }
                    searchParamsRawIgnoreRef.current = true;
                    return newParams;
                },
                { replace: true }
            );
        },
        [setSearchParamsRaw, setStore]
    );

    // NOTE: A field allow-list for setSearchParams/onChangeSearchParams was evaluated but
    // intentionally NOT implemented. useQueryState is a generic hook (<T extends object>)
    // shared by four different pages, each with a different field set (statuses, status,
    // companyName, companyId). The TypeScript generic `T` already enforces the field names
    // at compile time. A runtime allow-list derived from `initialState` would require
    // calling the function form, would not cover functional updaters, and would not add
    // meaningful security beyond what the backend ownership check already provides.
    const onChangeSearchParams = useCallback(
        (e: ChangeEvent | NamedValue | SelectChangeEvent<unknown>) => {
            const nv = extractEvent(e);
            if (nv) {
                setSearchParams((old) => ({ ...old, [nv[0]]: nv[1] }));
            }
        },
        [setSearchParams]
    );

    const searchParamsRef = useRef(searchParams);
    searchParamsRef.current = searchParams;

    // `getSearchParams` is still consumed by CompanyOverviewPage, which reads the ref
    // inside a debounced search callback. No other page uses it.
    const getSearchParams = useCallback(() => searchParamsRef.current, []);

    /**
     * Stable ref that always holds the latest `setSearchParams` function.
     *
     * Using a ref keeps the mount effect's dependency array empty (`[]`) without
     * suppressing the exhaustive-deps lint rule: a `useRef` value is stable, so
     * the effect captures the ref object itself (stable) and reads `.current`
     * (always up-to-date) at call time.
     */
    const setSearchParamsRef = useRef(setSearchParams);
    setSearchParamsRef.current = setSearchParams;

    useEffect(() => {
        if (searchParamsRawIgnoreRef.current) {
            searchParamsRawIgnoreRef.current = false;
        } else {
            setSearchParamTrigger((t) => t + 1);
        }
    }, [searchParamsRaw]);

    /**
     * Post-mount effect that writes the seed state to the URL.
     *
     * The `searchParamsRef` is already seeded synchronously during the first render
     * (above), so `getSearchParams()` returns the restored value immediately.
     * This effect only synchronises the URL — the trigger suppression via
     * `searchParamsRawIgnoreRef` ensures no spurious `searchParamTrigger` increment
     * (avoiding the double-fetch on `CompanyOverviewPage`).
     *
     * `setSearchParamsRef` is a stable ref — its identity never changes, so the
     * empty `[]` dep array is lint-clean without any suppression comment.
     */
    useEffect(() => {
        if (seedStateRef.current != null) {
            setSearchParamsRef.current(seedStateRef.current);
            // Clear the seed after it has been applied so that subsequent renders
            // with an empty URL compute a fresh state via parseSearchParams instead
            // of returning the stale first-mount seed (e.g. when the user manually
            // removes all query params after having had a non-default filter set).
            seedStateRef.current = undefined;
        }
    }, []);

    return { searchParams, onChangeSearchParams, setSearchParams, getSearchParams, searchParamTrigger };
};
